import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { AppContext } from '../../contexts/app-context';
import { HtmlContent } from '../../global-component-styles';
import GlobalStyles from '../../global-styles';
import {
  addressValidation, apiRequestError, filterCountryStates, isValidJSONString, setAllFieldTouched
} from '../../utils/helper';
import { ClearIcon, DoneIcon } from '../../utils/icons';
import {
  Box, Button, FormControl, FormControlLabel, Grid, Hidden, InputLabel, Paper, Select, Switch,
  TextField, Typography
} from '../../utils/material-ui-core';

interface AddressType {
  firmName: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  locality1: string;
  locality2: string;
  region1: string;
  region2: string;
  postalCode: string;
  country: string;
  allowPObox: boolean;
}

const addressValidationDefaultValue: AddressType = {
  firmName: "",
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  locality1: "",
  locality2: "",
  region1: "",
  region2: "",
  postalCode: "",
  country: "USA",
  allowPObox: true,
};

const addressFormValidationSchema = Yup.object().shape({
  addressLine1: Yup.string().required("Address line 1 is required."),
  locality1: Yup.string().required("City is required."),
  region1: Yup.string().required("State is required."),
  postalCode: Yup.string().required("Postal Code is required."),
  country: Yup.string().required("Country is required."),
});

const AddressValidator = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { referenceLookups } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const defaultAddressResultOutput: AddressResultOutput = {
    valid: false,
    message: "",
    address: {
      addressline1: "",
      addressline2: "",
      addressline3: "",
      country: "",
      locality1: "",
      locality2: "",
      postalCode: "",
      postalCodeFull: "",
      region1: "",
      region2: "",
    },
    addressLine: "",
    cityStatePostalCountry: "",
    errorCodeTagged: false,
    latitude: 0,
    longitude: 0,
    poBox: false,
    autoCorrect: false,
    sameAddressLine: false,
    addressType: "",
    show: false,
    skip: false,
  };
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [result, setResult] = useState<AddressResultOutput>(defaultAddressResultOutput);
  const [initialAddressValidationFormValues, setInitialAddressValidationFormValues] = useState(addressValidationDefaultValue);
  const [states, setStates] = useState<State[]>([]);

  const handleCountryChanged = (event: any) => {
    setStates(filterCountryStates(referenceLookups.countries, event.target.value));
  };

  const submitAddressValidation = async (values: AddressType, actions: any) => {
    const payload = {
      address: {
        addressline1: values.addressLine1,
        addressline2: values.addressLine2,
        addressline3: "",
        country: values.country,
        locality1: values.locality1,
        locality2: "",
        postalCode: values.postalCode,
        region1: values.region1,
        region2: "",
      },
      allowPOBox: values.allowPObox,
      firmName: values.firmName,
    };

    setShowResult(false);
    setErrorMessage("");
    setResult(defaultAddressResultOutput);
    setLoading(true);
    try {
      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;
          if (address.valid) {
            const addressResultOutput: AddressResultOutput = {
              valid: address.valid,
              message: address.friendlyMessage,
              address: address,
              addressLine: address.addressline1 + " " + address.addressline2,
              cityStatePostalCountry: address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country,
              errorCodeTagged: address.errorCodeTagged,
              latitude: address.latitude,
              longitude: address.longitude,
              poBox: address.poBox,
              autoCorrect: address.autoCorrect,
              sameAddressLine: address.sameAddressLine,
              addressType: address.addressType,
              show: true,
              skip: false,
            };

            if (values.postalCode.length > 5) {
              addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCodeFull + " " + address.country;
            }
            setResult(addressResultOutput);
            setShowResult(true);
          } else {
            setErrorMessage(address.friendlyMessage);
            setShowResult(true);
          }
          setLoading(false);
        }
      } else {
        setErrorMessage(apiResponse.errorMessage);
        setLoading(false);
      }
    } catch (e) {
      setErrorMessage(apiRequestError(e, "Something was wrong in our system."));
      setLoading(false);
    }
  };

  const openGoogle = (address: AddressType) => {
    try {
      const url = "http://google.com/search?q=" + address.addressLine1 + " " + address.addressLine2 + " " + address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;
      window.open(url, "SensorAdminGoogle", "noopener,noreferrer");
    } catch (e) { }
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      const tempAddressValidationDefaultValue = addressValidationDefaultValue;
      setInitialAddressValidationFormValues(tempAddressValidationDefaultValue);
      setStates(filterCountryStates(referenceLookups.countries, tempAddressValidationDefaultValue.country));
    };
    initialPageAsync();
  }, [referenceLookups]);

  return (
    <Box className={pageStyles.validatorContainer}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box className={globalStyles.topic}>Address Validator</Box>
        </Grid>
      </Grid>
      <Box p={2} className={pageStyles.inputContainer}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={6}>
            <Formik innerRef={formReference} initialValues={initialAddressValidationFormValues} validationSchema={addressFormValidationSchema} validateOnBlur={true} validateOnChange={true} onSubmit={submitAddressValidation}>
              {({ values, errors, handleChange, handleReset, setSubmitting, touched, handleBlur, isSubmitting }) => (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitAddressValidation(values, { setSubmitting });
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={6}>
                      <TextField
                        id="firmName"
                        label="Firm Name"
                        value={values.firmName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.firmName && Boolean(errors.firmName)}
                        helperText={errors.firmName && touched.firmName ? errors.firmName : null}
                      />
                    </Grid>
                    <Hidden only={["xs", "sm", "md"]}>
                      <Grid item lg={6}></Grid>
                    </Hidden>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                      <TextField
                        required
                        id="addressLine1"
                        name="addressLine1"
                        label="Address Line 1"
                        value={values.addressLine1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.addressLine1 && Boolean(errors.addressLine1)}
                        helperText={errors.addressLine1 && touched.addressLine1 ? errors.addressLine1 : null}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                      <TextField
                        id="addressLine2"
                        name="addressLine2"
                        label="Address Line 2"
                        value={values.addressLine2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={6} lg={6}>
                      <TextField
                        required
                        id="locality1"
                        label="City"
                        value={values.locality1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.locality1 && Boolean(errors.locality1)}
                        helperText={errors.locality1 && touched.locality1 ? errors.locality1 : null}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} lg={6}>
                      <FormControl required className={globalStyles.formControl} fullWidth={true}>
                        <InputLabel id="state-label" shrink={true}>
                          State
                        </InputLabel>
                        <Select native labelId="state-label" id="region1" name="region1" value={values.region1} onChange={handleChange} label="State/Province" error={touched.region1 && Boolean(errors.region1)}>
                          <option value=""></option>
                          {states.map((state, index) => (
                            <option value={state.code} key={index}>
                              {state.code + "-" + state.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4} md={6} lg={6}>
                      <TextField
                        required
                        id="postalCode"
                        label="Postal Code"
                        value={values.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={touched.postalCode && Boolean(errors.postalCode)}
                        helperText={errors.postalCode && touched.postalCode ? errors.postalCode : null}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={6} lg={6}>
                      <FormControl required className={globalStyles.formControl} fullWidth={true}>
                        <InputLabel id="country-label" shrink={true}>
                          Country
                        </InputLabel>
                        <Select
                          native
                          labelId="country-label"
                          id="country"
                          name="country"
                          value={values.country}
                          onChange={(e) => {
                            handleCountryChanged(e);
                            handleChange(e);
                          }}
                          label="Country"
                          error={touched.country && Boolean(errors.country)}
                        >
                          <option value=""></option>
                          {referenceLookups.countries.map((country, index) => (
                            <option value={country.code} key={index}>
                              {country.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}></Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <FormControlLabel control={<Switch checked={values.allowPObox} onChange={handleChange} name="allowPObox" />} label="Allow P.O Box" />
                    </Grid>
                  </Grid>

                  <Box p={1}>&nbsp;</Box>
                  <Box p={3} pt={3} display="flex" justifyContent="center">
                    <Button
                      type="button"
                      id="button-clear-address"
                      color="default"
                      disabled={loading}
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        handleReset();
                        setShowResult(false);
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      id="button-save-address"
                      variant="contained"
                      color="secondary"
                      disabled={loading}
                      className={globalStyles.buttonDefault}
                      onClick={() => {
                        setAllFieldTouched(formReference, "", values);
                      }}
                    >
                      {loading ? "Checking..." : "Validate"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            {showResult ? (
              <>
                <Paper elevation={0} className={pageStyles.resultContainer}>
                  <Typography gutterBottom variant="h6">
                    Result
                  </Typography>
                  {errorMessage === "" ? (
                    <>
                      <Box className={pageStyles.successMessage}>
                        <Typography gutterBottom variant="h6" align="center">
                          <HtmlContent dangerouslySetInnerHTML={{ __html: result.message }}></HtmlContent>
                        </Typography>
                      </Box>

                      <Typography gutterBottom variant="body1" align="center">
                        <b>{result.addressLine}</b>
                      </Typography>
                      <Typography gutterBottom variant="body1" align="center">
                        <b>{result.cityStatePostalCountry}</b>
                      </Typography>

                      <Box>&nbsp;</Box>
                      <Grid container spacing={3}>
                        <Grid item xs={6} sm={4} md={6} lg={4} xl={4}>
                          <Typography variant="h6" color="secondary" align="center">
                            {result.latitude}
                          </Typography>
                          <Typography variant="body2" align="center">
                            Latitude
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={6} lg={4} xl={4}>
                          <Typography variant="h6" color="secondary" align="center">
                            {result.longitude}
                          </Typography>
                          <Typography variant="body2" align="center">
                            Longitude
                          </Typography>
                        </Grid>

                        <Grid item xs={6} sm={4} md={12} lg={4} xl={4}>
                          <Typography variant="h6" color="secondary" align="center">
                            {result.addressType}
                          </Typography>
                          <Typography variant="body2" align="center">
                            Address Type
                          </Typography>
                        </Grid>

                        <Grid item xs={6} sm={6} md={6} lg={3} xl={3}>
                          <Box display="flex" p={0} justifyContent="center" alignItems="center">
                            {result.autoCorrect ? <DoneIcon color="secondary" /> : <ClearIcon color="secondary" />}
                          </Box>
                          <Typography gutterBottom variant="body2" align="center">
                            Auto Correct
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={3} xl={3}>
                          <Box display="flex" p={0} justifyContent="center" alignItems="center">
                            {result.sameAddressLine ? <DoneIcon color="secondary" /> : <ClearIcon color="secondary" />}
                          </Box>
                          <Typography gutterBottom variant="body2" align="center">
                            Same Address Line
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={3} xl={3}>
                          <Box display="flex" p={0} justifyContent="center" alignItems="center">
                            {result.poBox ? <DoneIcon color="secondary" /> : <ClearIcon color="secondary" />}
                          </Box>
                          <Typography gutterBottom variant="body2" align="center">
                            Is P.O Box{" "}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={3} xl={3}>
                          <Box display="flex" p={0} justifyContent="center" alignItems="center">
                            {result.errorCodeTagged ? <DoneIcon color="secondary" /> : <ClearIcon color="secondary" />}
                          </Box>
                          <Typography gutterBottom variant="body2" align="center">
                            Error Code Tagged
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box>&nbsp;</Box>

                      <Box display="flex" justifyContent="center" alignContent="center">
                        <Button
                          type="submit"
                          id="button-save-address"
                          variant="contained"
                          color="secondary"
                          disabled={loading}
                          className={globalStyles.buttonDefault}
                          onClick={() => {
                            openGoogle(formReference.current.values);
                          }}
                        >
                          Open In Google
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Box className={pageStyles.errorMessage}>
                      <Typography gutterBottom variant="h6" align="center">
                        <HtmlContent dangerouslySetInnerHTML={{ __html: errorMessage }}></HtmlContent>
                      </Typography>
                    </Box>
                  )}
                  <Box p={3}>&nbsp;</Box>
                </Paper>
              </>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    validatorContainer: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(4),
      height: "100%",
    },
    inputContainer: {
      color: theme.palette.type === "dark" ? "#ffffff" : "#000000",
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      margin: theme.spacing(0),
      height: "calc(100% - 70px)",
    },
    resultContainer: {
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
      padding: 8,
    },
    errorMessage: {
      color: theme.palette.error.main,
    },
    successMessage: {
      color: theme.palette.success.main,
    },
  })
);

export default AddressValidator;
